/**
 * SPA Pageview tracking for Mosaic (DAP-114765).
 *
 * Editorial brands (e.g. edition.cnn.com) are single-page apps: article-to-
 * article navigation never triggers a document load, so a load-only Pageview
 * misses every virtual pageview after the first.
 *
 * We hook the History API and emit ONE Pageview per distinct
 * `origin + pathname`. Deduping on `origin + pathname` rather than the full URL
 * is deliberate: editorial pages churn the query string and hash for live
 * updates/ad refreshes, which is what previously caused a stream of repeated
 * Pageviews on a single page.
 */

export const MOSAIC_LOCATION_CHANGE_EVENT = 'psmlocationchange';

/** The dedupe key for a pageview: origin + pathname, ignoring query and hash. */
export function pageviewKey(win: Window | any = window): string {
  try {
    return `${win.location.origin}${win.location.pathname}`;
  } catch {
    return '';
  }
}

/**
 * Emit a Mosaic Pageview on SPA route changes.
 *
 * Seeded with the current location, so the route that produced the initial
 * load-time Pageview does not fire a second one. Returns a teardown function.
 */
export function installMosaicSpaPageviews(onPageview: () => void, win: any = window): () => void {
  let lastKey = pageviewKey(win);

  const handleLocationChange = () => {
    const key = pageviewKey(win);
    if (!key || key === lastKey) {
      return;
    }
    lastKey = key;
    onPageview();
  };

  // NavigationTracker may already patch history and dispatch the same event;
  // patching once from our side keeps us correct either way, and the pathname
  // dedupe above absorbs any duplicate dispatch.
  const history: any = win.history;
  if (history && !history.__psmMosaicPatched) {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args: unknown[]) {
      originalPushState.apply(history, args);
      win.dispatchEvent(new Event(MOSAIC_LOCATION_CHANGE_EVENT));
    };
    history.replaceState = function (...args: unknown[]) {
      originalReplaceState.apply(history, args);
      win.dispatchEvent(new Event(MOSAIC_LOCATION_CHANGE_EVENT));
    };
    history.__psmMosaicPatched = true;
  }

  win.addEventListener('popstate', handleLocationChange);
  win.addEventListener(MOSAIC_LOCATION_CHANGE_EVENT, handleLocationChange);

  return () => {
    win.removeEventListener('popstate', handleLocationChange);
    win.removeEventListener(MOSAIC_LOCATION_CHANGE_EVENT, handleLocationChange);
  };
}
